import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Contact } from '@/types/Contact';
import { Conversation } from '@/types/Conversation';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import ContactDisplay from '@/components/common/ContactDisplay';
import ContactSheet from './ContactSheet';
import { ExternalLink, Phone, MessageSquare, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDateFormat } from '@/hooks/useDateFormat';

interface ContactsTableProps {
  contacts: Contact[];
  conversations: Conversation[];
  agentId: number;
  sortConfig: {
    key: keyof Contact;
    direction: 'ascending' | 'descending';
  } | null;
  onSort: (key: keyof Contact) => void;
  onUpdateContact: (contactId: number, name: string) => Promise<void>;
}

const ContactsTable: React.FC<ContactsTableProps> = ({
  contacts,
  conversations,
  agentId,
  onSort,
  onUpdateContact,
}) => {
  const { t } = useTranslation();
  const df = useDateFormat();
  const isMobile = useIsMobile();
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Helper function to find the last conversation for a contact
  const getLastConversationDate = (contact: Contact): string | null => {
    const contactConversations = conversations.filter(
      conv => conv.caller_id === contact.caller_id
    );

    if (contactConversations.length === 0) return null;

    const lastConversation = contactConversations.reduce((latest, current) => {
      return new Date(current.created_at) > new Date(latest.created_at)
        ? current
        : latest;
    });

    return lastConversation.created_at;
  };

  const handleRowClick = (contact: Contact) => {
    setSelectedContact(contact);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedContact(null);
  };

  const handleSaveContact = async (contactId: number, name: string) => {
    await onUpdateContact(contactId, name);
  };

  if (contacts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 text-4xl text-muted-foreground">
          {t('contacts.no_contacts.translation', 'No contacts found')}
        </div>
        <div className="text-xl text-muted-foreground">
          {t(
            'contacts.no_contacts_description.translation',
            'Contacts will appear here once they are added to this agent'
          )}
        </div>
      </div>
    );
  }

  // Mobile card layout
  if (isMobile) {
    return (
      <Card className="p-2">
        <div className="space-y-1">
          {contacts.map((contact: Contact, index) => {
            return (
              <div
                key={contact.id}
                className={`flex cursor-pointer items-center justify-between rounded-xl p-3 transition-colors hover:bg-muted/40 ${
                  index < contacts.length - 1 ? 'border-b border-border' : ''
                }`}
                onClick={() => handleRowClick(contact)}
              >
                <div className="flex min-w-0 flex-1 items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <span className="truncate">
                    <ContactDisplay
                      callerId={contact.caller_id}
                      agentId={agentId}
                      contacts={[contact]}
                      variant="compact"
                    />
                  </span>
                </div>
                <div className="flex flex-shrink-0 items-center gap-3">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MessageSquare className="h-4 w-4" />
                    <span className="font-semibold text-foreground">
                      {contact.conversations_count || 0}
                    </span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            );
          })}
        </div>

        <ContactSheet
          contact={selectedContact}
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          onSave={handleSaveContact}
          conversations={conversations}
          agentId={agentId}
        />
      </Card>
    );
  }

  const contactsTableColGroup = (
    <colgroup>
      <col className="w-[24%]" />
      <col className="w-[20%]" />
      <col className="w-[26%]" />
      <col className="w-[14%]" />
      <col className="w-[16%]" />
    </colgroup>
  );

  const contactsTableHeader = (
    <TableHeader className="[&_tr]:border-b-0 [&_tr:hover]:bg-transparent dark:[&_tr:hover]:bg-transparent">
      <TableRow className="hover:bg-transparent dark:hover:bg-transparent">
        <TableHead className="py-1">
          <Button
            className="text-sm"
            variant="link"
            onClick={() => onSort('name')}
          >
            {t('contacts.name.translation', 'Name')}
          </Button>
        </TableHead>
        <TableHead className="py-1 text-center hidden lg:table-cell">
          <Button
            className="text-sm"
            variant="link"
            onClick={() => onSort('caller_id')}
          >
            {t('contacts.phone_number.translation', 'Phone Number')}
          </Button>
        </TableHead>
        <TableHead className="py-1 text-center">
          <Button
            className="text-sm"
            variant="link"
            onClick={() => onSort('last_conversation_at')}
          >
            {t('contacts.last_conversation.translation', 'Last Conversation')}
          </Button>
        </TableHead>
        <TableHead className="py-1">
          <Button
            variant="link"
            onClick={() => onSort('conversations_count')}
            className="flex items-center gap-2 mx-auto text-sm"
          >
            {t('contacts.conversations.translation', 'Conversations')}
          </Button>
        </TableHead>
        <TableHead className="text-right py-1 hidden xl:table-cell">
          <Button
            variant="link"
            className="text-sm"
            onClick={() => onSort('created_at')}
          >
            {t('contacts.added_on.translation', 'Added On')}
          </Button>
        </TableHead>
      </TableRow>
    </TableHeader>
  );

  // Desktop table layout
  return (
    <>
      <div className="flex flex-col gap-1">
        <div className="px-6">
          <Table className="table-fixed">
            {contactsTableColGroup}
            {contactsTableHeader}
          </Table>
        </div>
        <Card className="px-4 py-2">
          <Table className="table-fixed border-separate [border-spacing:0_0.125rem]">
            {contactsTableColGroup}
            <TableBody>
              {contacts.map((contact: Contact) => (
                <TableRow
                  key={contact.id}
                  className="group cursor-pointer border-b-0 hover:bg-transparent dark:hover:bg-transparent"
                  onClick={() => handleRowClick(contact)}
                >
                  <TableCell className="rounded-l-full px-4 py-3 text-sm text-foreground transition-colors group-hover:bg-muted/40">
                    <div className="flex items-center gap-2">
                      <ContactDisplay
                        callerId={contact.caller_id}
                        agentId={agentId}
                        contacts={[contact]}
                        variant="default"
                        className={
                          !contact.name
                            ? 'text-muted-foreground'
                            : 'text-foreground'
                        }
                      />
                      <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                    </div>
                  </TableCell>
                  <TableCell className="hidden px-4 py-3 text-center text-sm text-foreground transition-colors group-hover:bg-muted/40 lg:table-cell">
                    {contact.caller_id}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-center text-sm text-foreground transition-colors group-hover:bg-muted/40">
                    {(() => {
                      const lastConversationDate =
                        getLastConversationDate(contact);
                      return lastConversationDate ? (
                        <span className="font-medium flex gap-2 items-center justify-center">
                          <span>{df.time(lastConversationDate)}</span>
                          <span>{df.date(lastConversationDate)}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          {t(
                            'contacts.no_conversations.translation',
                            'No conversations'
                          )}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="rounded-r-full px-4 py-3 text-center text-sm text-foreground transition-colors group-hover:bg-muted/40 xl:rounded-r-none">
                    <span className="inline-flex items-center justify-center px-1 font-medium text-foreground">
                      {contact.conversations_count || 0}
                    </span>
                  </TableCell>
                  <TableCell className="hidden rounded-r-full px-4 py-3 text-right text-sm text-foreground transition-colors group-hover:bg-muted/40 xl:table-cell">
                    {df.date(contact.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <ContactSheet
        contact={selectedContact}
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveContact}
        conversations={conversations}
        agentId={agentId}
      />
    </>
  );
};

export default ContactsTable;
